'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function NumberSequenceCalculator() {
  const t = useTranslations("tools.numberSequenceCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Arithmetic sequence state
  const [arithmeticInputs, setArithmeticInputs] = useState({
    firstTerm: 2,
    commonDiff: 5,
    nthTerm: 20
  });
  const [arithmeticResults, setArithmeticResults] = useState<any>(null);

  // Geometric sequence state
  const [geometricInputs, setGeometricInputs] = useState({
    firstTerm: 2,
    commonRatio: 5,
    nthTerm: 12
  });
  const [geometricResults, setGeometricResults] = useState<any>(null);

  // Fibonacci sequence state
  const [fibonacciInput, setFibonacciInput] = useState(10);
  const [fibonacciResults, setFibonacciResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const formatNumber = (num: number): string => {
    if (Math.abs(num) > 1e10) {
      return num.toExponential(4);
    }
    if (num % 1 === 0) {
      return num.toString();
    }
    return parseFloat(num.toFixed(6)).toString();
  };

  const calculateArithmetic = (e: React.FormEvent) => {
    e.preventDefault();
    const { firstTerm, commonDiff, nthTerm } = arithmeticInputs;

    if (nthTerm < 1) {
      alert(t('errorPositiveInteger'));
      return;
    }

    const nth = firstTerm + commonDiff * (nthTerm - 1);
    const sum = (nthTerm * (firstTerm + nth)) / 2;
    const sequence = [];
    const displayCount = Math.min(nthTerm, 20);

    for (let i = 1; i <= displayCount; i++) {
      sequence.push(firstTerm + commonDiff * (i - 1));
    }

    setArithmeticResults({
      nth,
      sum,
      sequence,
      hasMore: nthTerm > 20
    });
  };

  const calculateGeometric = (e: React.FormEvent) => {
    e.preventDefault();
    const { firstTerm, commonRatio, nthTerm } = geometricInputs;

    if (nthTerm < 1) {
      alert(t('errorPositiveInteger'));
      return;
    }

    const nth = firstTerm * Math.pow(commonRatio, nthTerm - 1);
    let sum;

    if (commonRatio === 1) {
      sum = firstTerm * nthTerm;
    } else {
      sum = firstTerm * (1 - Math.pow(commonRatio, nthTerm)) / (1 - commonRatio);
    }

    const sequence = [];
    const displayCount = Math.min(nthTerm, 20);

    for (let i = 1; i <= displayCount; i++) {
      sequence.push(firstTerm * Math.pow(commonRatio, i - 1));
    }

    setGeometricResults({
      nth,
      sum,
      sequence,
      hasMore: nthTerm > 20
    });
  };

  const calculateFibonacci = (e: React.FormEvent) => {
    e.preventDefault();

    if (fibonacciInput < 0) {
      alert(t('errorNonNegative'));
      return;
    }

    if (fibonacciInput > 1000) {
      alert(t('errorMaxValue'));
      return;
    }

    const sequence = [0, 1];

    for (let i = 2; i <= fibonacciInput; i++) {
      sequence.push(sequence[i - 1] + sequence[i - 2]);
    }

    const nth = sequence[fibonacciInput];
    const sum = sequence.slice(0, fibonacciInput + 1).reduce((acc, val) => acc + val, 0);
    const displaySequence = sequence.slice(0, Math.min(fibonacciInput + 1, 20));

    setFibonacciResults({
      nth,
      sum,
      sequence: displaySequence,
      hasMore: fibonacciInput >= 20
    });
  };

  const clearArithmetic = () => {
    setArithmeticInputs({ firstTerm: 0, commonDiff: 0, nthTerm: 0 });
    setArithmeticResults(null);
  };

  const clearGeometric = () => {
    setGeometricInputs({ firstTerm: 0, commonRatio: 0, nthTerm: 0 });
    setGeometricResults(null);
  };

  const clearFibonacci = () => {
    setFibonacciInput(0);
    setFibonacciResults(null);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    purple: '#7c3aed',
    purpleHover: '#6d28d9',
    orange: '#f59e0b',
    orangeHover: '#d97706',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    {}
  );

  const toolTitleStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const sequenceCardsStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }
  );

  const sequenceCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const cardH3Style = getResponsiveStyle(
    {
      fontWeight: 600,
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const sequenceDefinitionStyle = {
    background: '#f8fafc',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    fontFamily: "'Courier New', monospace",
    fontSize: '0.9rem',
    color: '#475569'
  };

  const sequenceExampleStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '1.5rem',
    fontStyle: 'italic' as const
  };

  const formGroupStyle = {
    marginBottom: '1.25rem'
  };

  const labelStyle = getResponsiveStyle(
    {
      display: 'block',
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem'
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.blueHover} 100%)`,
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginBottom: '0.5rem'
    },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'background 0.2s'
    },
    { padding: '0.75rem', fontSize: '0.95rem' },
    { padding: '0.75rem', fontSize: '0.95rem' },
    { padding: '0.75rem', fontSize: '0.95rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      marginTop: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH4Style = getResponsiveStyle(
    {
      color: colors.textPrimary,
      marginBottom: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
    borderLeft: `4px solid ${colors.blue}`
  };

  const resultLabelStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: '#475569'
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '0.95rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    {
      fontWeight: 700,
      color: colors.textPrimary,
      fontFamily: "'Courier New', monospace"
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const sequenceDisplayStyle = {
    background: '#f0f9ff',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    fontFamily: "'Courier New', monospace",
    fontSize: '0.9rem',
    color: '#0c4a6e',
    wordWrap: 'break-word' as const,
    lineHeight: 1.6
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem'
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    {
      lineHeight: 1.8,
      color: colors.textSecondary,
      paddingLeft: '1.5rem',
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem'
    },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.25rem' }
  );

  const refListStyle = {
    listStyle: 'none',
    padding: 0
  };

  const refItemStyle = {
    marginBottom: '0.75rem'
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-all' as const,
    fontSize: '0.95rem'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={toolHeaderStyle}>
            <h2 style={toolTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <div style={sequenceCardsStyle}>
            <div style={sequenceCardStyle}>
              <h3 style={{ ...cardH3Style, color: colors.blue }}>
                <span style={{ fontSize: '1.75rem' }}>📊</span> {t('arithmeticTitle')}
              </h3>
              <div style={sequenceDefinitionStyle}>{t('arithmeticFormula')}</div>
              <div style={sequenceExampleStyle}>{t('arithmeticExample')}</div>

              <form onSubmit={calculateArithmetic}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('arithmeticFirstTerm')}</label>
                  <input
                    type="number"
                    step="any"
                    value={arithmeticInputs.firstTerm}
                    onChange={(e) => setArithmeticInputs({...arithmeticInputs, firstTerm: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('arithmeticCommonDiff')}</label>
                  <input
                    type="number"
                    step="any"
                    value={arithmeticInputs.commonDiff}
                    onChange={(e) => setArithmeticInputs({...arithmeticInputs, commonDiff: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('arithmeticNthTerm')}</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={arithmeticInputs.nthTerm}
                    onChange={(e) => setArithmeticInputs({...arithmeticInputs, nthTerm: parseInt(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>
                <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={clearArithmetic}>{t('clearButton')}</button>
              </form>

              {arithmeticResults && (
                <div style={resultsStyle}>
                  <h4 style={resultsH4Style}>✨ {t('resultsTitle')}</h4>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('nthTermLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(arithmeticResults.nth)}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('sumLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(arithmeticResults.sum)}</span>
                  </div>
                  <div style={sequenceDisplayStyle}>
                    {arithmeticResults.sequence.map(formatNumber).join(', ')}
                    {arithmeticResults.hasMore && ', ... ' + t('showingFirst20')}
                  </div>
                </div>
              )}
            </div>

            <div style={sequenceCardStyle}>
              <h3 style={{ ...cardH3Style, color: colors.purple }}>
                <span style={{ fontSize: '1.75rem' }}>📈</span> {t('geometricTitle')}
              </h3>
              <div style={sequenceDefinitionStyle}>{t('geometricFormula')}</div>
              <div style={sequenceExampleStyle}>{t('geometricExample')}</div>

              <form onSubmit={calculateGeometric}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('geometricFirstTerm')}</label>
                  <input
                    type="number"
                    step="any"
                    value={geometricInputs.firstTerm}
                    onChange={(e) => setGeometricInputs({...geometricInputs, firstTerm: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('geometricCommonRatio')}</label>
                  <input
                    type="number"
                    step="any"
                    value={geometricInputs.commonRatio}
                    onChange={(e) => setGeometricInputs({...geometricInputs, commonRatio: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('geometricNthTerm')}</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={geometricInputs.nthTerm}
                    onChange={(e) => setGeometricInputs({...geometricInputs, nthTerm: parseInt(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                </div>
                <button type="submit" style={{ ...calculateBtnStyle, background: `linear-gradient(135deg, ${colors.purple} 0%, ${colors.purpleHover} 100%)` }}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={clearGeometric}>{t('clearButton')}</button>
              </form>

              {geometricResults && (
                <div style={resultsStyle}>
                  <h4 style={resultsH4Style}>✨ {t('resultsTitle')}</h4>
                  <div style={{ ...resultItemStyle, borderLeftColor: colors.purple }}>
                    <span style={resultLabelStyle}>{t('nthTermLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(geometricResults.nth)}</span>
                  </div>
                  <div style={{ ...resultItemStyle, borderLeftColor: colors.purple }}>
                    <span style={resultLabelStyle}>{t('sumLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(geometricResults.sum)}</span>
                  </div>
                  <div style={sequenceDisplayStyle}>
                    {geometricResults.sequence.map(formatNumber).join(', ')}
                    {geometricResults.hasMore && ', ... ' + t('showingFirst20')}
                  </div>
                </div>
              )}
            </div>

            <div style={sequenceCardStyle}>
              <h3 style={{ ...cardH3Style, color: colors.orange }}>
                <span style={{ fontSize: '1.75rem' }}>🌀</span> {t('fibonacciTitle')}
              </h3>
              <div style={sequenceDefinitionStyle}>{t('fibonacciFormula')}</div>
              <div style={sequenceExampleStyle}>{t('fibonacciExample')}</div>

              <form onSubmit={calculateFibonacci}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('fibonacciNthTerm')}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={fibonacciInput}
                    onChange={(e) => setFibonacciInput(parseInt(e.target.value) || 0)}
                    style={inputStyle}
                    required
                  />
                </div>
                <button type="submit" style={{ ...calculateBtnStyle, background: `linear-gradient(135deg, ${colors.orange} 0%, ${colors.orangeHover} 100%)` }}>{t('calculateButton')}</button>
                <button type="button" style={clearBtnStyle} onClick={clearFibonacci}>{t('clearButton')}</button>
              </form>

              {fibonacciResults && (
                <div style={resultsStyle}>
                  <h4 style={resultsH4Style}>✨ {t('resultsTitle')}</h4>
                  <div style={{ ...resultItemStyle, borderLeftColor: colors.orange }}>
                    <span style={resultLabelStyle}>{t('nthTermLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(fibonacciResults.nth)}</span>
                  </div>
                  <div style={{ ...resultItemStyle, borderLeftColor: colors.orange }}>
                    <span style={resultLabelStyle}>{t('sumLabel')}</span>
                    <span style={resultValueStyle}>{formatNumber(fibonacciResults.sum)}</span>
                  </div>
                  <div style={sequenceDisplayStyle}>
                    {fibonacciResults.sequence.map(formatNumber).join(', ')}
                    {fibonacciResults.hasMore && ', ... ' + t('showingFirst20')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={{ marginBottom: '0.75rem' }}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('arithmeticDetailsTitle')}</h3>
        <p style={paragraphStyle}>{t('arithmeticDetails')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('geometricDetailsTitle')}</h3>
        <p style={paragraphStyle}>{t('geometricDetails')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('fibonacciDetailsTitle')}</h3>
        <p style={paragraphStyle}>{t('fibonacciDetails')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('applicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t('application1')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('application2')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('application3')}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t('application4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={refListStyle}>
          <li style={refItemStyle}>
            <a href="https://allonrobots.com/number-sequence-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.omnicalculator.com/math/arithmetic-sequence" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://testbook.com/calculators/arithmetic-sequence-calculator" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.storyofmathematics.com/math-calculators/geometric-sequence-calculator/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={refItemStyle}>
            <a href="https://www.calculator.net/number-sequence-calculator.html" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}